import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Upload, Trash2, Download, File } from 'lucide-react';
import Button from '../ui/Button';
import { uploadFile, deleteFile, loadAssets, saveAssets } from '../../utils/storage';

const DocumentsList = ({ assetId, documents = [], onUpdate, onBeforeUpload }) => {
  const [localDocuments, setLocalDocuments] = useState(documents || []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync localDocuments with documents prop when it changes
  useEffect(() => {
    setLocalDocuments(documents || []);
  }, [documents]);

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return 'PDF';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'DOC';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return 'XLS';
    return 'FILE';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Ensure asset exists before uploading (for new assets)
    let currentAssetId = assetId;
    if (!assetId && onBeforeUpload) {
      const asset = await onBeforeUpload();
      if (!asset || !asset.id) {
        return;
      }
      currentAssetId = asset.id;
    }

    if (!currentAssetId) {
      alert('Asset must be saved before uploading documents.');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadFile(currentAssetId, file, 'document');
      const newDocuments = [...localDocuments, result.file];
      setLocalDocuments(newDocuments);
      
      // Update asset in storage
      const assets = await loadAssets();
      const asset = assets.find(a => a.id === currentAssetId);
      if (asset) {
        asset.documents = newDocuments;
        await saveAssets(assets);
        if (onUpdate) onUpdate(newDocuments);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      await deleteFile(assetId, document.filename, 'document');
      
      const newDocuments = localDocuments.filter(doc => doc.filename !== document.filename);
      setLocalDocuments(newDocuments);

      // Update asset in storage
      const assets = await loadAssets();
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        asset.documents = newDocuments;
        await saveAssets(assets);
        if (onUpdate) onUpdate(newDocuments);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document: ' + error.message);
    }
  };

  const handleDownload = (document) => {
    window.open(document.path, '_blank');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-foreground">Documents</h4>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            id="document-upload"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center space-x-2"
          >
            {uploading ? (
              <>Uploading...</>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                <span>Upload</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {localDocuments.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-body text-sm">No documents uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {localDocuments.map((document, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/20 rounded flex items-center justify-center">
                    <File className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {document.originalName || document.filename}
                    </p>
                    <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                      <span>{getFileIcon(document.type)}</span>
                      <span>{formatFileSize(document.size)}</span>
                      {document.uploadDate && (
                        <span>
                          {new Date(document.uploadDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(document)}
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDocument(document)}
                    className="text-destructive hover:bg-destructive/10"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default DocumentsList;

